import { useState, useEffect, useRef } from "react";
import { Table, Input, Select, DatePicker, Skeleton, Empty, Tooltip } from "antd";
import { SearchOutlined, FilterOutlined, ClearOutlined, CalendarOutlined, UserOutlined } from "@ant-design/icons";
import Pagination from "@mui/material/Pagination";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import EnquiryFollowUp from "./EnquiryFollowUp";
import {
  useInboxLeads,
  useLeadDetailsFollowUp,
  useFollowUpOptions,
} from "../hooks/useManageEnquiry";

const PAGE_SIZE = 10;

const POSITIVE_STYLES = {
  "1": { bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0", label: "Positive" },
  "2": { bg: "#fef2f2", color: "#dc2626", border: "#fecaca", label: "Negative" },
  "0": { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe", label: "Neutral" },
};

// Lead stage from the `status` column ("7" = converted, "4" = future, "2" = negative).
// This wins over is_positive: the API puts the same code in is_positive too, and a
// value like 7 there is not a positive/negative/neutral sentiment — reading it as
// one is what made a converted lead render as "Neutral".
const LEAD_STATUS_STYLES = {
  "7": { bg: "#dcfce7", color: "#15803d", border: "#86efac", label: "Done" },
  "4": { bg: "#fffbeb", color: "#d97706", border: "#fde68a", label: "Future" },
  "2": { bg: "#fef2f2", color: "#dc2626", border: "#fecaca", label: "Negative" },
};

const resolveLeadStatus = (row) => {
  const byStatus = LEAD_STATUS_STYLES[String(row?.status ?? "")];
  if (byStatus) return byStatus;
  return POSITIVE_STYLES[String(row?.is_positive ?? "0")] || POSITIVE_STYLES["0"];
};

const FOLLOWUP_STATUS_STYLE = {
  "0":  { bg: "#eff6ff",  color: "#2563eb", border: "#bfdbfe" },
  "1":  { bg: "#fff1f2",  color: "#e11d48", border: "#fecdd3" },
  "2":  { bg: "#f5f5f5",  color: "#6b7280", border: "#d1d5db" },
  "3":  { bg: "#f0fdf4",  color: "#16a34a", border: "#bbf7d0" },
  "4":  { bg: "#fffbeb",  color: "#d97706", border: "#fde68a" },
  "5":  { bg: "#fef2f2",  color: "#dc2626", border: "#fecaca" },
  "6":  { bg: "#f5f3ff",  color: "#7c3aed", border: "#ddd6fe" },
  "7":  { bg: "#ecfdf5",  color: "#059669", border: "#6ee7b7" },
  "8":  { bg: "#fff7ed",  color: "#ea580c", border: "#fed7aa" },
  "9":  { bg: "#f1f5f9",  color: "#475569", border: "#cbd5e1" },
  "10": { bg: "#fef2f2",  color: "#dc2626", border: "#fecaca" },
};

const EMPTY_FILTER = { search: "", start_date: null, end_date: null, is_positive: "" };

const thStyle = {
  fontSize: "12px", fontWeight: 700, color: "#475569",
  textTransform: "uppercase", letterSpacing: "0.6px", whiteSpace: "nowrap",
};

export default function AssignedLeads() {
  const [page, setPage]                     = useState(1);
  const [filter, setFilter]                 = useState({ ...EMPTY_FILTER });
  const [appliedFilter, setAppliedFilter]   = useState({ ...EMPTY_FILTER });
  const [filtersOpen, setFiltersOpen]       = useState(true);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [followUpEnquiry, setFollowUpEnquiry] = useState(null);
  const debounceRef = useRef(null);

  /* ── search debounce ── */
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setAppliedFilter((p) => ({ ...p, search: filter.search }));
      setPage(1);
    }, 500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [filter.search]);

  /* ── data ── */
  const { data, isFetching, isError } = useInboxLeads(page, {
    search:      appliedFilter.search,
    start_date:  appliedFilter.start_date || "",
    end_date:    appliedFilter.end_date   || "",
    is_positive: appliedFilter.is_positive,
  });

  useEffect(() => { if (isError) toast.error("Failed to load assigned leads"); }, [isError]);

  const leads = data?.data  || [];
  const total = data?.total || 0;

  /* ── follow-up status labels ── */
  const { data: fuOptsRaw = [] } = useFollowUpOptions();
  const fuOpts = Array.isArray(fuOptsRaw) ? fuOptsRaw : (fuOptsRaw?.data || []);
  const fuLabelMap = Object.fromEntries(fuOpts.map((o) => [String(o.option_value), o.label]));

  /* ── lead detail → open EnquiryFollowUp ── */
  const { data: leadDetails } = useLeadDetailsFollowUp(selectedLeadId);
  useEffect(() => {
    if (!leadDetails) return;
    setFollowUpEnquiry({
      id:             leadDetails.id,
      name:           leadDetails.name,
      email:          leadDetails.email,
      phone:          leadDetails.phone,
      company:        leadDetails.company,
      address:        leadDetails.address,
      message:        leadDetails.message,
      status:
        leadDetails.is_positive === 1 ? "Positive"
        : leadDetails.is_positive === 2 ? "Negative"
        : "Neutral",
      assignToId:      leadDetails.assign_to      || null,
      assignedToName:  leadDetails.assign_to_name || "Unassigned",
      stage:           leadDetails.stage_name,
      followUps:       leadDetails.follow_ups     || [],
    });
  }, [leadDetails]);

  /* ── back-button closes panel ── */
  useEffect(() => {
    if (!followUpEnquiry) return;
    window.history.pushState({ fuOpen: true }, "");
    const onPop = () => setFollowUpEnquiry(null);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [followUpEnquiry]);

  /* ── helpers ── */
  const activeFilterCount = [
    appliedFilter.is_positive,
    appliedFilter.start_date,
    appliedFilter.end_date,
  ].filter(Boolean).length;

  const handleApply = () => { setAppliedFilter({ ...filter }); setPage(1); };
  const handleClear = () => {
    setFilter({ ...EMPTY_FILTER });
    setAppliedFilter({ ...EMPTY_FILTER });
    setPage(1);
  };

  /* ── follow-up due badge ── */
  const getDueBadge = (nfd) => {
    if (!nfd) return { label: "No date", bg: "#f9fafb", color: "#9ca3af", border: "#e5e7eb" };
    const d = dayjs(nfd, "DD-MM-YYYY");
    if (!d.isValid()) return { label: "No date", bg: "#f9fafb", color: "#9ca3af", border: "#e5e7eb" };
    const today = dayjs().startOf("day");
    if (d.isBefore(today)) return { label: "Overdue", bg: "#fef2f2", color: "#dc2626", border: "#fecaca" };
    if (d.isSame(today))   return { label: "Due Today", bg: "#fffbeb", color: "#d97706", border: "#fde68a" };
    return { label: d.format("DD-MM-YYYY"), bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0" };
  };

  /* ── columns ── */
  const columns = [
    {
      title: <span style={{ ...thStyle }}>#</span>,
      key: "index", width: 50, align: "center",
      render: (_, __, idx) => (
        <span style={{ fontSize: 13, color: "#9ca3af", fontWeight: 600 }}>
          {(page - 1) * PAGE_SIZE + idx + 1}
        </span>
      ),
    },
    {
      title: <span style={thStyle}>Lead Name</span>,
      key: "name",
      render: (row) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: "50%", background: "linear-gradient(135deg,#0891b2,#0e7490)", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
            {(row.name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
          </div>
          <div>
            <div
              onClick={() => setSelectedLeadId(row.id)}
              style={{ fontWeight: 700, color: "#0891b2", cursor: "pointer", fontSize: 14 }}
            >
              {row.name || "—"}
            </div>
            {row.company && <div style={{ fontSize: 11, color: "#9ca3af" }}>{row.company}</div>}
          </div>
        </div>
      ),
    },
    {
      title: <span style={thStyle}>Email</span>,
      dataIndex: "email", key: "email",
      render: (v) => <span style={{ fontSize: 13, color: "#374151" }}>{v || "—"}</span>,
    },
    {
      title: <span style={thStyle}>Phone</span>,
      dataIndex: "phone", key: "phone",
      render: (v) => <span style={{ fontSize: 13, color: "#374151", fontWeight: 600 }}>{v || "—"}</span>,
    },
    {
      title: <span style={thStyle}>Status</span>,
      key: "lead_status",
      render: (_, row) => {
        const s = resolveLeadStatus(row);
        return (
          <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 600 }}>
            {s.label}
          </span>
        );
      },
    },
    {
      title: <span style={thStyle}>Follow-up Status</span>,
      key: "followup_status",
      render: (row) => {
        // The list API sends this as follow_up_status; lead-detail sends enquiry_status
        const raw = row.enquiry_status ?? row.follow_up_status;
        const val  = String(raw ?? "0");
        const label = fuLabelMap[val] || raw || "—";
        const s    = FOLLOWUP_STATUS_STYLE[val] || FOLLOWUP_STATUS_STYLE["0"];
        return (
          <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 600 }}>
            {label}
          </span>
        );
      },
    },
    {
      title: <span style={thStyle}>Next Follow-up</span>,
      dataIndex: "next_followup_date", key: "next_followup_date",
      render: (v) => {
        const b = getDueBadge(v);
        return (
          <span style={{ background: b.bg, color: b.color, border: `1px solid ${b.border}`, borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>
            {b.label}
          </span>
        );
      },
    },
    {
      title: <span style={thStyle}>Assigned To</span>,
      dataIndex: "assign_to_name", key: "assign_to_name",
      render: (v) => <span style={{ fontSize: 13, color: "#374151" }}>{v || "—"}</span>,
    },
    {
      title: <span style={thStyle}>Action</span>,
      key: "action",
      render: (row) => (
        <Tooltip title="View / Follow-up">
          <button
            onClick={() => setSelectedLeadId(row.id)}
            style={{ padding: "5px 14px", borderRadius: 8, border: "1px solid #bae6fd", background: "#f0f9ff", color: "#0891b2", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
          >
            Follow-up
          </button>
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">

          {/* ── Header ── */}
          <div style={{ background: "linear-gradient(135deg,#0891b2 0%,#0e7490 100%)", borderRadius: 16, padding: "28px 32px", marginBottom: 24, boxShadow: "0 8px 32px rgba(8,145,178,0.28)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ width: 56, height: 56, background: "rgba(255,255,255,0.18)", borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26 }}>
                <UserOutlined style={{ color: "white" }} />
              </div>
              <div>
                <h4 style={{ color: "white", margin: 0, fontSize: 24, fontWeight: 700 }}>Assigned Leads</h4>
                <p style={{ color: "rgba(255,255,255,0.85)", margin: 0, fontSize: 14 }}>
                  Your assigned leads with pending or overdue follow-ups
                </p>
              </div>
            </div>
            {total > 0 && (
              <span style={{ background: "rgba(255,255,255,0.2)", color: "white", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 20, padding: "5px 16px", fontSize: 14, fontWeight: 700 }}>
                {total} leads
              </span>
            )}
          </div>

          {/* ── Filters ── */}
          <div style={{ background: "white", borderRadius: 14, border: "1px solid #e5e7eb", boxShadow: "0 2px 12px rgba(0,0,0,0.05)", marginBottom: 20, overflow: "hidden" }}>
            <div
              onClick={() => setFiltersOpen((o) => !o)}
              style={{ padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", background: "#fafbff", borderBottom: filtersOpen ? "1px solid #e5e7eb" : "none" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <FilterOutlined style={{ color: "#0891b2", fontSize: 16 }} />
                <span style={{ fontWeight: 700, fontSize: 15, color: "#1f2937" }}>Filters</span>
                {activeFilterCount > 0 && (
                  <span style={{ background: "#0891b2", color: "white", borderRadius: 20, padding: "1px 10px", fontSize: 12, fontWeight: 700 }}>
                    {activeFilterCount} active
                  </span>
                )}
              </div>
              <span style={{ color: "#6b7280", fontSize: 13 }}>{filtersOpen ? "▲ Collapse" : "▼ Expand"}</span>
            </div>

            {filtersOpen && (
              <div style={{ padding: "20px 24px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14, marginBottom: 16 }}>
                  <Input
                    prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
                    placeholder="Search name, email, phone..."
                    value={filter.search}
                    onChange={(e) => setFilter((f) => ({ ...f, search: e.target.value }))}
                    style={{ borderRadius: 9, height: 40 }}
                    allowClear
                  />
                  <Select
                    value={filter.is_positive || undefined}
                    onChange={(v) => setFilter((f) => ({ ...f, is_positive: v || "" }))}
                    placeholder="All Statuses"
                    allowClear
                    style={{ width: "100%", height: 40 }}
                  >
                    <Select.Option value="1">Positive</Select.Option>
                    <Select.Option value="0">Neutral</Select.Option>
                    <Select.Option value="2">Negative</Select.Option>
                  </Select>
                  <DatePicker
                    placeholder="Start Date"
                    format="DD/MM/YYYY"
                    value={filter.start_date ? dayjs(filter.start_date, "YYYY-MM-DD") : null}
                    onChange={(_, s) => setFilter((f) => ({ ...f, start_date: s ? dayjs(s, "DD/MM/YYYY").format("YYYY-MM-DD") : null }))}
                    style={{ width: "100%", height: 40, borderRadius: 9 }}
                    suffixIcon={<CalendarOutlined style={{ color: "#9ca3af" }} />}
                  />
                  <DatePicker
                    placeholder="End Date"
                    format="DD/MM/YYYY"
                    value={filter.end_date ? dayjs(filter.end_date, "YYYY-MM-DD") : null}
                    onChange={(_, s) => setFilter((f) => ({ ...f, end_date: s ? dayjs(s, "DD/MM/YYYY").format("YYYY-MM-DD") : null }))}
                    style={{ width: "100%", height: 40, borderRadius: 9 }}
                    suffixIcon={<CalendarOutlined style={{ color: "#9ca3af" }} />}
                  />
                </div>
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                  <button onClick={handleClear} style={{ height: 38, padding: "0 20px", borderRadius: 9, border: "1px solid #e5e7eb", background: "white", color: "#6b7280", fontWeight: 600, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                    <ClearOutlined /> Clear
                  </button>
                  <button onClick={handleApply} style={{ height: 38, padding: "0 24px", borderRadius: 9, border: "none", background: "linear-gradient(135deg,#0891b2,#0e7490)", color: "white", fontWeight: 700, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, boxShadow: "0 2px 8px rgba(8,145,178,0.3)" }}>
                    <FilterOutlined /> Apply Filters
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ── Table ── */}
          <div style={{ background: "white", borderRadius: 16, boxShadow: "0 2px 16px rgba(0,0,0,0.07)", border: "1px solid #f0f0f0", overflow: "hidden" }}>
            <div style={{ padding: "18px 24px", borderBottom: "2px solid #f5f5f5", display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 38, height: 38, background: "linear-gradient(135deg,#0891b2,#0e7490)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 17 }}>
                <UserOutlined />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: "#1f2937" }}>My Assigned Leads</div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  {total} lead{total !== 1 ? "s" : ""} with pending / overdue follow-ups
                </div>
              </div>
            </div>

            {isFetching ? (
              <div style={{ padding: 24 }}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} active paragraph={{ rows: 1 }} style={{ marginBottom: 14 }} />
                ))}
              </div>
            ) : leads.length === 0 ? (
              <div style={{ padding: "60px 24px", textAlign: "center" }}>
                <Empty description={<span style={{ color: "#9ca3af" }}>No assigned leads found</span>} />
              </div>
            ) : (
              <div className="table-responsive">
                <Table
                  dataSource={leads}
                  columns={columns}
                  rowKey="id"
                  pagination={false}
                  loading={false}
                  onRow={(row) => ({
                    style: { cursor: "pointer" },
                    onClick: () => setSelectedLeadId(row.id),
                    onMouseEnter: (e) => { e.currentTarget.style.background = "#f0f9ff"; },
                    onMouseLeave: (e) => { e.currentTarget.style.background = ""; },
                  })}
                  style={{ borderRadius: 0 }}
                  size="middle"
                />
              </div>
            )}

            {!isFetching && total > PAGE_SIZE && (
              <div style={{ padding: "16px 24px", borderTop: "1px solid #f0f0f0", display: "flex", justifyContent: "center", background: "#fafbff" }}>
                <Pagination
                  count={Math.ceil(total / PAGE_SIZE)}
                  page={page}
                  onChange={(_, v) => setPage(v)}
                  color="primary"
                />
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ── EnquiryFollowUp panel — full-screen overlay above the app chrome,
             same wrapper All Leads / Inbox / Future use. Without it the panel
             renders inside .main-content and the sidebar clips its left column. */}
      {followUpEnquiry && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1050, background: "#f1f5f9", animation: "slideInRight .28s ease" }}>
          <EnquiryFollowUp
            enquiry={followUpEnquiry}
            onClose={() => { setFollowUpEnquiry(null); setSelectedLeadId(null); }}
          />
        </div>
      )}
    </div>
  );
}
